/*
 * Workspace-app Administration surface — full-page admin experience
 * mounted at /app/administration. Wraps the existing admin pages
 * (DHIS2 instances, members, billing, settings, etc.) inside the
 * AdministrationShell layout so the user gets a focused configuration
 * workspace instead of fighting with a collapsible <details> sidebar.
 */

import { Navigate, Route, Routes } from "react-router-dom";
import { AuthSession } from "../lib/api.js";
import { useWorkspacePermissions } from "../lib/usePermissions.js";
import AdministrationShell, { AdminCategory } from "./AdministrationShell.js";

import DhisInstances from "../pages/DhisInstances.js";
import AppClients from "../pages/AppClients.js";
import Forwarding from "../pages/Forwarding.js";
import Team from "../pages/Team.js";
import Subscriptions from "../pages/Subscriptions.js";
import Billing from "../pages/Billing.js";
import SettingsPage from "../pages/Settings.js";
import Security from "../pages/Security.js";
import ComingSoon from "../pages/ComingSoon.js";

const BASE = "/app/administration";

export default function WorkspaceAdministrationArea({ session }: { session: AuthSession }) {
  const { has, hasAny } = useWorkspacePermissions(session.workspaceId);
  const canDhis2 = has("workspace.dhis2_connections.view");
  const canForwarding = has("workspace.forwarding.view");
  const canMembers = has("workspace.members.view");
  const canBilling = hasAny("workspace.billing.view", "workspace.billing.manage", "workspace.subscription.manage");
  const canSecurity = hasAny("workspace.security.view", "workspace.security.manage");
  const canSettings = hasAny("workspace.settings.view", "workspace.settings.manage");

  // Categories drive both the top-tab menubar AND the URL structure
  // (/app/administration/<categoryId>/<sub>). The first visible item
  // inside a category is the landing page when its top-tab is clicked.
  const categories: AdminCategory[] = [
    {
      id: "connections",
      label: "Connections",
      show: canDhis2 || canForwarding,
      items: [
        { to: `${BASE}/connections/instances`,   label: "DHIS2 instances",   show: canDhis2 },
        { to: `${BASE}/connections/app-clients`, label: "DHIS2 app clients", show: canDhis2 },
        { to: `${BASE}/connections/forwarding`,  label: "Forwarding",        show: canForwarding }
      ]
    },
    {
      id: "workspace",
      label: "Workspace",
      show: canMembers || canBilling,
      items: [
        { to: `${BASE}/workspace/team`,          label: "Members & roles",   show: canMembers },
        { to: `${BASE}/workspace/subscriptions`, label: "Subscription",      show: canBilling },
        { to: `${BASE}/workspace/billing`,       label: "Billing & invoices", show: canBilling }
      ]
    },
    {
      id: "settings",
      label: "Settings",
      show: canSettings || canSecurity,
      items: [
        { to: `${BASE}/settings/general`,  label: "General",  show: canSettings },
        { to: `${BASE}/settings/security`, label: "Security", show: canSecurity }
      ]
    }
  ];

  return (
    <AdministrationShell
      session={session}
      basePath={BASE}
      brandLabel="TallyStream · Workspace administration"
      categories={categories}
      backTo={{ to: "/app", label: "Back to workspace" }}
    >
      <Routes>
        <Route index element={<Navigate to="connections/instances" replace />} />
        <Route path="connections" element={<Navigate to="connections/instances" replace />} />
        <Route path="connections/instances" element={<DhisInstances />} />
        <Route path="connections/app-clients" element={<AppClients />} />
        <Route path="connections/forwarding" element={<Forwarding />} />
        <Route path="workspace" element={<Navigate to="workspace/team" replace />} />
        <Route path="workspace/team" element={<Team />} />
        <Route path="workspace/subscriptions" element={<Subscriptions />} />
        <Route path="workspace/billing" element={<Billing />} />
        <Route path="settings" element={<Navigate to="settings/general" replace />} />
        <Route path="settings/general" element={<SettingsPage />} />
        <Route path="settings/security" element={<Security />} />
        {/* Catch-all renders the structured stub for nav items whose
            page hasn't shipped yet — keeps the IA fully reachable. */}
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    </AdministrationShell>
  );
}
